// Password policy, mirrored from Portal-BE app/warehouse/components/PasswordPolicy.js.
// Keep the two in sync: the BE is the gate, this is what the checklist shows.
//
//   • Admin / Manager / Customer Service ("strong"): 14+ characters, uppercase,
//                                   lowercase, digit, symbol (access/positions
//                                   usesStrongPassword; Customer Service since 2026-10-20).
//   • everyone else ("memorable"):  9+ characters, at least one letter and one digit.
//                                   Capitals and symbols are allowed, not required.
//   • both: no spaces, and nothing easily guessable (helpers/passwordStrength:
//     common passwords, abcd/1234/qwer runs, repeats, the person's own
//     username or name).
//   • age (2026-10-20): every password expires PASSWORD_MAX_AGE_DAYS (90) after
//     it was set. The BE forces a new one at the next sign-in; passwordExpired
//     below mirrors the rule for the Users-page "Password expired" badge.
//
// The style also picks the generator: a random strong string for the strong
// positions, an adjective + noun + two digits ("bluetote42") in the UI language
// for everyone else — something an operative can remember and type on a Zebra.

import { usesStrongPassword } from "../../access/positions";
import { wordListsFor } from "./passwordWords";
import { weakPasswordCode, weakPasswordKey, type PasswordContext } from "./passwordStrength";

export type { PasswordContext } from "./passwordStrength";

export const MIN_LENGTH_MANAGER_PLUS = 14;
export const MIN_LENGTH_STANDARD = 9;

/** Every password expires this many days after it was set (BE PasswordPolicy.js
 *  PASSWORD_MAX_AGE_DAYS). The BE enforces it at sign-in; this mirror only drives
 *  the Users-page badge and the expired wording on /change-password. */
export const PASSWORD_MAX_AGE_DAYS = 90;
export const PASSWORD_MAX_AGE_MS = PASSWORD_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

/** True when the stamp is more than PASSWORD_MAX_AGE_DAYS before `now`, or
 *  missing / unreadable — the BE treats "never stamped" as expired too. */
export const passwordExpired = (passwordChangedAt: string | Date | null | undefined, now: number = Date.now()): boolean => {
    if (passwordChangedAt === null || passwordChangedAt === undefined || passwordChangedAt === "") return true;
    const stamped = new Date(passwordChangedAt).getTime();
    if (!Number.isFinite(stamped)) return true;
    return now - stamped > PASSWORD_MAX_AGE_MS;
};

export interface PasswordPolicy {
    style: "strong" | "memorable";
    minLength: number;
    requireLetter: boolean;
    requireUpper: boolean;
    requireLower: boolean;
    requireDigit: boolean;
    requireSymbol: boolean;
}

export const STRONG_POLICY: PasswordPolicy = Object.freeze({
    style: "strong", minLength: MIN_LENGTH_MANAGER_PLUS,
    requireLetter: true, requireUpper: true, requireLower: true, requireDigit: true, requireSymbol: true,
});

export const MEMORABLE_POLICY: PasswordPolicy = Object.freeze({
    style: "memorable", minLength: MIN_LENGTH_STANDARD,
    requireLetter: true, requireUpper: false, requireLower: false, requireDigit: true, requireSymbol: false,
});

/** Policy for a position. Unknown / missing → the stricter rule, so a
 *  misread session shows the harder checklist rather than letting a short
 *  password through where 14 was expected. */
export const passwordPolicyFor = (position: unknown): PasswordPolicy => {
    if (position === null || position === undefined || position === "") return STRONG_POLICY;
    return usesStrongPassword(position) ? STRONG_POLICY : MEMORABLE_POLICY;
};

export const minPasswordLengthFor = (position: unknown): number => passwordPolicyFor(position).minLength;

export interface RuleCheck {
    key: string;
    labelKey: string;
    pass: boolean;
}

/** Only the rules the policy actually requires — an operative never sees
 *  "Uppercase letter" or "Symbol" greyed out as if they were needed. The
 *  last rule, "not easily guessable", names the specific problem while it
 *  fails (too common / a run / repeated / your username / your name). */
export const evaluatePolicy = (password: string, policy: PasswordPolicy, context: PasswordContext = {}): RuleCheck[] => {
    const rules: RuleCheck[] = [
        { key: "length", labelKey: "change_password_rule_length", pass: password.length >= policy.minLength },
    ];
    if (policy.requireLetter && !(policy.requireUpper && policy.requireLower)) {
        rules.push({ key: "letter", labelKey: "change_password_rule_letter", pass: /[a-zA-Z]/.test(password) });
    }
    if (policy.requireUpper) rules.push({ key: "upper", labelKey: "change_password_rule_upper", pass: /[A-Z]/.test(password) });
    if (policy.requireLower) rules.push({ key: "lower", labelKey: "change_password_rule_lower", pass: /[a-z]/.test(password) });
    if (policy.requireDigit) rules.push({ key: "digit", labelKey: "change_password_rule_digit", pass: /[0-9]/.test(password) });
    if (policy.requireSymbol) rules.push({ key: "symbol", labelKey: "change_password_rule_symbol", pass: /[^a-zA-Z0-9\s]/.test(password) });
    rules.push({ key: "nows", labelKey: "change_password_rule_no_spaces", pass: password.length > 0 && !/\s/.test(password) });
    const weak = password.length > 0 ? weakPasswordCode(password, context) : null;
    rules.push({
        key: "guessable",
        labelKey: weak ? weakPasswordKey(weak) : "change_password_rule_not_guessable",
        pass: password.length > 0 && weak === null,
    });
    return rules;
};

export const isPolicyCompliant = (password: string, policy: PasswordPolicy, context: PasswordContext = {}): boolean =>
    evaluatePolicy(password, policy, context).every(r => r.pass);

// Strong-generator alphabet: no look-alikes (0/O/o, 1/l/I) and only symbols
// on the first symbol layer of the Zebra / Gboard keyboards.
const UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const LOWER = "abcdefghijkmnpqrstuvwxyz";
const DIGIT = "23456789";
const SYMBOL = "!@#$%&*?+=";
const ALL = UPPER + LOWER + DIGIT + SYMBOL;

// Uniform integer in [0, n) via rejection sampling — no modulo bias.
const randomIndex = (n: number): number => {
    const cryptoObj = (typeof globalThis !== "undefined" ? (globalThis as any).crypto : undefined) as Crypto | undefined;
    if (cryptoObj?.getRandomValues) {
        const limit = Math.floor(0x100000000 / n) * n;
        const buf = new Uint32Array(1);
        for (;;) {
            cryptoObj.getRandomValues(buf);
            if (buf[0] < limit) return buf[0] % n;
        }
    }
    // Test / very old environments only — never the production path.
    return Math.floor(Math.random() * n);
};

const pick = <T,>(list: readonly T[]): T => list[randomIndex(list.length)];
const pickChar = (alphabet: string): string => alphabet[randomIndex(alphabet.length)];

/** A random strong password of max(minLength, 12) characters (Manager+). */
export const generateStrongPassword = (minLength: number, context: PasswordContext = {}): string => {
    const length = Math.max(minLength, 12);
    for (let attempt = 0; attempt < 20; attempt++) {
        const chars = [pickChar(UPPER), pickChar(LOWER), pickChar(DIGIT), pickChar(SYMBOL)];
        while (chars.length < length) chars.push(pickChar(ALL));
        // Fisher–Yates
        for (let i = chars.length - 1; i > 0; i--) {
            const j = randomIndex(i + 1);
            [chars[i], chars[j]] = [chars[j], chars[i]];
        }
        const candidate = chars.join("");
        if (isPolicyCompliant(candidate, STRONG_POLICY, context)) return candidate;
    }
    return `${pickChar(UPPER)}${pickChar(LOWER)}${pickChar(DIGIT)}${pickChar(SYMBOL)}`.padEnd(length, "x");
};

/** adjective + noun + two digits in the UI language, e.g. "bluetote42" /
 *  "zielonykot17": lowercase ASCII, between minLength and 18 characters,
 *  never one the guessability check would refuse (it retries). Roughly
 *  2^20 combinations — fine against online guessing, which the per-IP
 *  limiter and per-account lockout throttle; it is an operative's handheld
 *  password, not an admin credential. */
export const generateMemorablePassword = (language: string, minLength: number = MIN_LENGTH_STANDARD, context: PasswordContext = {}): string => {
    const { adjectives, nouns } = wordListsFor(language);
    for (let attempt = 0; attempt < 40; attempt++) {
        const number = String(10 + randomIndex(90));
        const candidate = `${pick(adjectives)}${pick(nouns)}${number}`;
        if (candidate.length < minLength || candidate.length > 18) continue;
        if (weakPasswordCode(candidate, context) !== null) continue;
        return candidate;
    }
    return `${pick(adjectives)}${pick(nouns)}${pick(nouns)}${10 + randomIndex(90)}`.slice(0, 18);
};

/** The right generator for a policy. */
export const generatePasswordFor = (policy: PasswordPolicy, language: string, context: PasswordContext = {}): string =>
    (policy.style === "strong"
        ? generateStrongPassword(policy.minLength, context)
        : generateMemorablePassword(language, policy.minLength, context));
